import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../core/api.service';
import { ConfirmDialogComponent } from '../core/confirm-dialog.component';

interface ReservationListItem {
  id: number;
  crsResId: string;
  firstName: string;
  lastName: string;
  arrivalDate: string | null;
  departureDate: string | null;
  roomNumber: string | null;
  pmsResId: string | null;
  vmLocalConfNumber: string | null;
  comments: string | null;
  vmSyncState: string;
  vmErrorId: string | null;
  vmLastError: string | null;
  statusLabel: string;
  canSend: boolean;
}

interface ReservationListResponse {
  total: number;
  page: number;
  perPage: number;
  pages: number;
  counts: { all: number; pending: number; sent: number; confirmed: number; failed: number; skipped: number };
  items: ReservationListItem[];
}

interface SendResult {
  ok: boolean;
  status: number | null;
  messageId: string;
  vmReservationId?: number;
  vmLocalConfNumber?: string | null;
  skipped?: boolean;
  updatedExisting?: boolean;
  requiresConfirmation?: boolean;
  needRelogin?: boolean;
  responseBody?: string;
  mergeEligibleLastNames?: string[];
}

interface MergeNameResult {
  lastName: string;
  getStatus: number | null;
  getCount: number;
  postStatus: number | null;
  merged: boolean;
  error: string | null;
}

interface MergeSummary {
  checkedLastNames: string[];
  namesChecked: number;
  mergeCallsAttempted: number;
  mergeCallsSucceeded: number;
  mergeCallsFailed: number;
  failures: MergeNameResult[];
  results: MergeNameResult[];
}

@Component({
  selector: 'app-reservations',
  imports: [FormsModule, ConfirmDialogComponent, RouterLink],
  template: `
    <section class="card">
      <header class="page-head">
        <div>
          <h1>Reservations</h1>
          <p class="muted">VM direct send queue and reservation status view.</p>
          @if (hotelCode()) {
            <div class="property-pill">Property {{ hotelCode() }}@if (hotelName()) { <span>· {{ hotelName() }}</span> }</div>
          }
        </div>
        <form class="actions" (submit)="applyFilters($event)">
          <button type="button" class="secondary" (click)="load()" [disabled]="loading()">Refresh</button>
          <a routerLink="/reservations/bulk" class="bulk-link">Send All Pending ({{ counts().pending }})</a>
        </form>
      </header>

      @if (message()) {
        <div class="flash">{{ message() }}</div>
      }

      <form class="filters" (submit)="applyFilters($event)">
        <label><span>Search</span><input [(ngModel)]="q" name="q" placeholder="CRSResID, name, email, room…"></label>
        <label><span>Arrival</span><input [(ngModel)]="arrFrom" name="arrFrom" type="date"></label>
        <label><span>To</span><input [(ngModel)]="arrTo" name="arrTo" type="date"></label>
        <label><span>Status</span>
          <select [(ngModel)]="status" name="status">
            <option value="">All</option>
            <option value="pending">Not Sent</option>
            <option value="sent">Sent (Prepared)</option>
            <option value="confirmed">Synced</option>
            <option value="failed">Errored</option>
            <option value="skipped">Skipped</option>
          </select>
        </label>
        <label><span>Rows</span>
          <select [(ngModel)]="perPage" name="perPage">
            <option [ngValue]="25">25</option>
            <option [ngValue]="50">50</option>
            <option [ngValue]="100">100</option>
            <option [ngValue]="200">200</option>
          </select>
        </label>
        <div class="filter-actions">
          <button type="submit">Apply</button>
          <button type="button" class="secondary" (click)="clearFilters()">Clear all</button>
        </div>
      </form>

      <div class="chips">
        <button type="button" class="chip" [class.active]="status === ''" (click)="setStatus('')">All <span>{{ counts().all }}</span></button>
        <button type="button" class="chip" [class.active]="status === 'pending'" (click)="setStatus('pending')">Not Sent <span>{{ counts().pending }}</span></button>
        <button type="button" class="chip" [class.active]="status === 'sent'" (click)="setStatus('sent')">Sent <span>{{ counts().sent }}</span></button>
        <button type="button" class="chip" [class.active]="status === 'confirmed'" (click)="setStatus('confirmed')">Synced <span>{{ counts().confirmed }}</span></button>
        <button type="button" class="chip" [class.active]="status === 'failed'" (click)="setStatus('failed')">Errored <span>{{ counts().failed }}</span></button>
        <button type="button" class="chip" [class.active]="status === 'skipped'" (click)="setStatus('skipped')">Skipped <span>{{ counts().skipped }}</span></button>
      </div>

      @if (loading()) {
        <div class="state">Loading reservations…</div>
      } @else if (items().length === 0) {
        <div class="state">No reservations match your filters.</div>
      } @else {
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                <th>CRSResID</th>
                <th>Guest</th>
                <th>Arrival</th>
                <th>Departure</th>
                <th>Room #</th>
                <th>PMS/VM</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              @for (r of items(); track r.id) {
                <tr>
                  <td>{{ r.crsResId }}</td>
                  <td>{{ r.firstName }} {{ r.lastName }}</td>
                  <td>{{ r.arrivalDate || '' }}</td>
                  <td>{{ r.departureDate || '' }}</td>
                  <td>{{ r.roomNumber || '' }}</td>
                  <td>
                    <div>{{ r.pmsResId || '' }}</div>
                    @if (r.vmLocalConfNumber) { <div class="sub">Local #: {{ r.vmLocalConfNumber }}</div> }
                  </td>
                  <td>
                    <span class="badge">{{ r.statusLabel }}</span>
                    @if (r.vmErrorId) { <div class="sub error-text">{{ r.vmErrorId }}</div> }
                  </td>
                  <td class="actions-cell">
                    <button type="button" class="secondary" (click)="view(r.id)" [disabled]="loading()">View</button>
                    <button type="button" (click)="sendOne(r)" [disabled]="!r.canSend || loading() || sendingId() === r.id">
                      {{ sendingId() === r.id ? 'Sending…' : 'Send' }}
                    </button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }

      <footer class="pager">
        <div>Page {{ page() }} of {{ pages() }} — {{ total() }} rows</div>
        <div class="pager-actions">
          <button type="button" class="secondary" (click)="goToPage(1)" [disabled]="page() <= 1">« First</button>
          <button type="button" class="secondary" (click)="goToPage(page() - 1)" [disabled]="page() <= 1">‹ Prev</button>
          <button type="button" class="secondary" (click)="goToPage(page() + 1)" [disabled]="page() >= pages()">Next ›</button>
          <button type="button" class="secondary" (click)="goToPage(pages())" [disabled]="page() >= pages()">Last »</button>
        </div>
      </footer>
    </section>

    @if (viewItem()) {
      <div class="overlay" (click)="closeView($event)">
        <div class="modal">
          <header><h2>Reservation {{ viewItem()?.crsResId }}</h2><button type="button" class="secondary" (click)="viewItem.set(null)">Close</button></header>
          <dl>
            <div><dt>Guest</dt><dd>{{ viewItem()?.firstName }} {{ viewItem()?.lastName }}</dd></div>
            <div><dt>Arrival</dt><dd>{{ viewItem()?.arrivalDate || '' }}</dd></div>
            <div><dt>Departure</dt><dd>{{ viewItem()?.departureDate || '' }}</dd></div>
            <div><dt>Room</dt><dd>{{ viewItem()?.roomNumber || '' }}</dd></div>
            <div><dt>Status</dt><dd>{{ viewItem()?.statusLabel }}</dd></div>
            <div><dt>PMSResID</dt><dd>{{ viewItem()?.pmsResId || '' }}</dd></div>
            <div><dt>VM Local</dt><dd>{{ viewItem()?.vmLocalConfNumber || '' }}</dd></div>
            <div><dt>Comments</dt><dd>{{ viewItem()?.comments || '' }}</dd></div>
            <div><dt>VM Error</dt><dd>{{ viewItem()?.vmLastError || viewItem()?.vmErrorId || '' }}</dd></div>
          </dl>
        </div>
      </div>
    }

    <app-confirm-dialog
      [open]="updateConfirmOpen()"
      title="Update Existing Reservation"
      message="This CRS reservation already exists in Visual Matrix. Send this record as an update to the existing VM reservation?"
      confirmLabel="Update Reservation"
      cancelLabel="Cancel"
      (confirm)="confirmExistingUpdate()"
      (cancel)="cancelExistingUpdate()"
    />
    <app-confirm-dialog
      [open]="mergeConfirmOpen()"
      title="Run Guest Duplicate Merge?"
      message="Would you like to run merge on potential guest history duplicates for this send?"
      confirmLabel="Yes, Run Merge"
      cancelLabel="No"
      (confirm)="confirmGuestMerge()"
      (cancel)="cancelGuestMerge()"
    />
    <app-confirm-dialog
      [open]="retryFailedConfirmOpen()"
      title="Retry Failed Reservation?"
      message="This reservation is currently errored. Clear the error state and retry send now?"
      confirmLabel="Clear Error + Retry"
      cancelLabel="Cancel"
      (confirm)="confirmRetryFailed()"
      (cancel)="cancelRetryFailed()"
    />

  `,
  styles: `
    .card{background:#fff;border:1px solid var(--line);border-radius:14px;padding:16px;display:grid;gap:14px}
    .page-head{display:flex;justify-content:space-between;gap:16px;align-items:flex-start;flex-wrap:wrap}
    h1,h2{margin:0}
    .muted{margin:4px 0 0;color:var(--muted)}
    .filters{display:grid;grid-template-columns:2fr repeat(4,minmax(0,1fr)) auto;gap:10px;align-items:end}
    label{display:grid;gap:6px}
    label span{font-size:12px;color:#4e5c75;text-transform:uppercase;letter-spacing:.04em}
    input,select{height:40px;border:1px solid #c8d2e1;border-radius:10px;padding:0 12px}
    .filter-actions,.actions,.actions-cell,.pager-actions{display:flex;gap:8px;flex-wrap:wrap}
    .chips{display:flex;gap:8px;flex-wrap:wrap}
    .chip{border:1px solid #d5deed;background:#f4f7fc;color:#24416a}
    .chip.active{background:#dfeafe}
    .chip span{margin-left:6px;font-weight:700}
    .bulk-link{height:38px;display:inline-flex;align-items:center;padding:0 12px;border-radius:10px;background:var(--primary);color:var(--primary-ink);font-weight:700;text-decoration:none}
    .property-pill{margin-top:8px;font-size:12px;color:#24416a;background:#edf3ff;border-radius:999px;display:inline-flex;padding:6px 10px;gap:6px}
    .property-pill span{color:#49668e}
    .flash{padding:10px;border-radius:8px;background:#eef4ff;color:#0a2f6b;border:1px solid #c7d7f5}
    .state{padding:18px;border:1px dashed var(--line);border-radius:12px;color:var(--muted);background:#f8fafc}
    .table-wrap{overflow:auto}
    table{width:100%;border-collapse:collapse;min-width:980px}
    th,td{padding:10px;border-bottom:1px solid #edf0f6;text-align:left;vertical-align:top}
    th{font-size:12px;text-transform:uppercase;letter-spacing:.4px;color:#4e5c75}
    .badge{display:inline-block;padding:2px 8px;border:1px solid #d5deed;border-radius:999px;background:#f4f7fc;font-size:12px}
    .sub{font-size:11px;color:#6a7487;margin-top:3px}
    .error-text{color:#991b1b}
    button{height:38px;border:0;border-radius:10px;background:var(--primary);color:var(--primary-ink);font-weight:700;cursor:pointer;padding:0 12px}
    .secondary{background:#6b778c}
    .pager{display:flex;justify-content:space-between;align-items:center;gap:16px;flex-wrap:wrap}
    .overlay{position:fixed;inset:0;background:rgba(15,29,54,.45);display:flex;justify-content:center;align-items:flex-start;padding:40px 16px;z-index:20}
    .modal{width:min(720px,100%);background:#fff;border-radius:16px;padding:18px;display:grid;gap:14px}
    .modal header{display:flex;justify-content:space-between;gap:12px;align-items:center}
    dl{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}
    dt{font-size:12px;color:#6a7487;text-transform:uppercase;letter-spacing:.05em}
    dd{margin:4px 0 0}
    @media (max-width: 980px){ .filters{grid-template-columns:1fr 1fr} }
    @media (max-width: 720px){ .filters,.pager,dl{grid-template-columns:1fr;display:grid} }
  `,
})
export class ReservationsComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);
  readonly loading = signal(true);
  readonly sendingId = signal<number | null>(null);
  readonly message = signal('');
  readonly items = signal<ReservationListItem[]>([]);
  readonly counts = signal<ReservationListResponse['counts']>({ all: 0, pending: 0, sent: 0, confirmed: 0, failed: 0, skipped: 0 });
  readonly total = signal(0);
  readonly page = signal(1);
  readonly pages = signal(1);
  readonly updateConfirmOpen = signal(false);
  readonly pendingUpdateItem = signal<ReservationListItem | null>(null);
  readonly retryFailedConfirmOpen = signal(false);
  readonly pendingRetryItem = signal<ReservationListItem | null>(null);
  readonly mergeConfirmOpen = signal(false);
  readonly pendingMergeReservationId = signal<number | null>(null);
  readonly viewItem = signal<ReservationListItem | null>(null);
  readonly hotelCode = signal('');
  readonly hotelName = signal('');
  readonly vmBaseUrl = signal('https://login.vmcloudpms.com');
  readonly vmPropertyId = signal('');
  readonly vmUsername = signal('');

  q = '';
  status = '';
  arrFrom = '';
  arrTo = '';
  perPage = 50;
  private sendPollHandle: ReturnType<typeof setInterval> | null = null;

  async ngOnInit(): Promise<void> {
    await this.loadContext();
    await this.load();
  }

  async loadContext(): Promise<void> {
    try {
      const [ctx, vm] = await Promise.all([
        firstValueFrom(this.api.get<{ activeProperty: { code: string; name: string; vmApiBaseUrl: string } | null }>('/context/property')),
        firstValueFrom(this.api.get<{ active: boolean; propertyId: string | null; username: string | null }>('/vm/session')),
      ]);
      this.hotelCode.set(ctx.activeProperty?.code || '');
      this.hotelName.set(ctx.activeProperty?.name || '');
      this.vmBaseUrl.set(ctx.activeProperty?.vmApiBaseUrl || 'https://login.vmcloudpms.com');
      this.vmPropertyId.set(vm.propertyId || ctx.activeProperty?.code || '');
      this.vmUsername.set(vm.username || '');
      window.dispatchEvent(new Event('settings-changed'));
    } catch {
      this.hotelCode.set('');
      this.hotelName.set('');
    }
  }

  async load(targetPage = this.page()): Promise<void> {
    this.loading.set(true);
    const params = new URLSearchParams({
      q: this.q,
      status: this.status,
      arr_from: this.arrFrom,
      arr_to: this.arrTo,
      page: String(targetPage),
      pp: String(this.perPage),
    });
    try {
      const response = await firstValueFrom(this.api.get<ReservationListResponse>(`/reservations?${params.toString()}`));
      this.items.set(response.items ?? []);
      this.counts.set(response.counts);
      this.total.set(response.total);
      this.page.set(response.page);
      this.pages.set(response.pages);
    } finally {
      this.loading.set(false);
    }
  }

  async applyFilters(event: Event): Promise<void> {
    event.preventDefault();
    await this.load(1);
  }

  async setStatus(status: string): Promise<void> {
    this.status = status;
    await this.load(1);
  }

  async clearFilters(): Promise<void> {
    this.q = '';
    this.status = '';
    this.arrFrom = '';
    this.arrTo = '';
    this.perPage = 50;
    await this.load(1);
  }

  async goToPage(page: number): Promise<void> {
    await this.load(page);
  }

  async view(reservationId: number): Promise<void> {
    const existing = this.items().find((x) => x.id === reservationId) ?? null;
    if (existing) this.viewItem.set(existing);
    try {
      const res = await firstValueFrom(this.api.get<{ item: ReservationListItem | null }>(`/reservations/${reservationId}`));
      if (res.item) this.viewItem.set(res.item);
    } catch {
      // Keep optimistic row data if detail fetch fails.
    }
  }

  closeView(event: MouseEvent): void {
    if (event.target === event.currentTarget) this.viewItem.set(null);
  }

  async sendOne(item: ReservationListItem): Promise<void> {
    if (!item.canSend) return;
    if (item.vmSyncState === 'failed') {
      this.pendingRetryItem.set(item);
      this.retryFailedConfirmOpen.set(true);
      return;
    }
    await this.executeSend(item, { onExisting: 'ask' });
  }

  async confirmRetryFailed(): Promise<void> {
    const item = this.pendingRetryItem();
    this.retryFailedConfirmOpen.set(false);
    this.pendingRetryItem.set(null);
    if (!item) return;
    await this.executeSend(item, { onExisting: 'ask' });
  }

  cancelRetryFailed(): void {
    this.retryFailedConfirmOpen.set(false);
    this.pendingRetryItem.set(null);
  }

  private async executeSend(item: ReservationListItem, payload: { onExisting?: 'ask' | 'skip' | 'update'; forceUpdate?: boolean }): Promise<void> {
    this.message.set('');
    this.sendingId.set(item.id);
    this.markRowSending(item.id);
    this.startLiveSendPolling(item.id);
    try {
      const first = await firstValueFrom(this.api.post<{ result: SendResult }>(`/reservations/${item.id}/send`, payload));
      if (first.result.requiresConfirmation) {
        this.stopLiveSendPolling();
        this.pendingUpdateItem.set(item);
        this.updateConfirmOpen.set(true);
        return;
      }
      if (first.result.needRelogin) {
        this.stopLiveSendPolling();
        await this.router.navigateByUrl('/import/vm-auth?next=reservations');
        return;
      }
      this.message.set(this.describeResult(first.result));
      this.queueMergePromptAfterSend(first.result, item.id);
    } catch {
      this.message.set('Send failed.');
    } finally {
      this.stopLiveSendPolling();
      this.sendingId.set(null);
      await this.load();
    }
  }

  async confirmExistingUpdate(): Promise<void> {
    const item = this.pendingUpdateItem();
    this.updateConfirmOpen.set(false);
    this.pendingUpdateItem.set(null);
    if (!item) return;

    this.sendingId.set(item.id);
    this.markRowSending(item.id);
    this.startLiveSendPolling(item.id);
    try {
      const second = await firstValueFrom(this.api.post<{ result: SendResult }>(`/reservations/${item.id}/send`, { forceUpdate: true, onExisting: 'update' }));
      if (second.result.needRelogin) {
        this.stopLiveSendPolling();
        await this.router.navigateByUrl('/import/vm-auth?next=reservations');
        return;
      }
      this.message.set(this.describeResult(second.result));
      this.queueMergePromptAfterSend(second.result, item.id);
    } catch {
      this.message.set('Send failed.');
    } finally {
      this.stopLiveSendPolling();
      this.sendingId.set(null);
      await this.load();
    }
  }

  cancelExistingUpdate(): void {
    this.updateConfirmOpen.set(false);
    this.pendingUpdateItem.set(null);
    this.message.set('Update canceled.');
  }

  async confirmGuestMerge(): Promise<void> {
    const reservationId = this.pendingMergeReservationId();
    this.mergeConfirmOpen.set(false);
    this.pendingMergeReservationId.set(null);
    if (!reservationId) return;

    this.sendingId.set(reservationId);
    this.message.set('');
    try {
      const response = await firstValueFrom(this.api.post<{ result: { needRelogin?: boolean; summary?: MergeSummary } }>(`/reservations/${reservationId}/merge-duplicates`, {}));
      if (response.result.needRelogin) {
        await this.router.navigateByUrl('/import/vm-auth?next=reservations');
        return;
      }
      this.message.set(this.describeMergeSummary(response.result.summary));
    } catch {
      this.message.set('Guest duplicate merge failed.');
    } finally {
      this.sendingId.set(null);
    }
  }

  cancelGuestMerge(): void {
    this.mergeConfirmOpen.set(false);
    this.pendingMergeReservationId.set(null);
  }

  private queueMergePromptAfterSend(result: SendResult, reservationId: number): void {
    if (!result.ok || result.skipped) return;
    const names = (result.mergeEligibleLastNames ?? []).map((x) => String(x ?? '').trim()).filter(Boolean);
    if (names.length === 0) return;
    this.pendingMergeReservationId.set(reservationId);
    this.mergeConfirmOpen.set(true);
  }

  private describeResult(result: SendResult): string {
    if (result.ok && result.skipped) {
      return `Skipped: already exists in VM${result.vmReservationId ? ` (VM ID ${result.vmReservationId})` : ''}.`;
    }
    if (result.ok && result.updatedExisting) {
      return `Updated existing VM reservation${result.vmReservationId ? ` ${result.vmReservationId}` : ''}.`;
    }
    if (result.ok) {
      return `Reservation sent${result.vmReservationId ? ` (VM ID ${result.vmReservationId})` : ''}.`;
    }
    if (result.needRelogin) {
      return 'Your VM session is not active. Sign in to Visual Matrix, then retry.';
    }
    return `Send failed${result.status ? ` (HTTP ${result.status})` : ''}${result.responseBody ? `: ${result.responseBody}` : '.'}`;
  }

  private describeMergeSummary(summary: MergeSummary | undefined): string {
    if (!summary) return 'Duplicate merge completed.';
    if (summary.namesChecked === 0) return 'Duplicate merge completed. No last names were eligible.';
    return `Duplicate merge complete: checked ${summary.namesChecked} last name(s), attempted ${summary.mergeCallsAttempted} merge call(s), succeeded ${summary.mergeCallsSucceeded}, failed ${summary.mergeCallsFailed}.`;
  }

  private markRowSending(reservationId: number): void {
    this.items.update((rows) =>
      rows.map((r) => (r.id === reservationId ? { ...r, vmSyncState: 'sending', statusLabel: 'Sending…' } : r)),
    );
    const view = this.viewItem();
    if (view?.id === reservationId) {
      this.viewItem.set({ ...view, vmSyncState: 'sending', statusLabel: 'Sending…' });
    }
  }

  private startLiveSendPolling(reservationId: number): void {
    this.stopLiveSendPolling();
    const poll = async (): Promise<void> => {
      try {
        const res = await firstValueFrom(this.api.get<{ item: ReservationListItem | null }>(`/reservations/${reservationId}`));
        const item = res.item;
        if (!item) return;
        this.items.update((rows) => rows.map((r) => (r.id === reservationId ? item : r)));
        const view = this.viewItem();
        if (view?.id === reservationId) this.viewItem.set(item);
      } catch {
        // Ignore transient polling errors; final load() will reconcile.
      }
    };
    void poll();
    this.sendPollHandle = setInterval(() => void poll(), 450);
  }

  private stopLiveSendPolling(): void {
    if (this.sendPollHandle) clearInterval(this.sendPollHandle);
    this.sendPollHandle = null;
  }
}
